import { z } from 'zod';
import { timeOfDaySchema } from './contacts.js';
import { callListSortRules } from './dialer.js';
/**
 * Import lists of the dialer (D-238…D-245, docs/dev/api-dialer.md „Importlisten"): the column fields of the
 * mapping step, the header detection, the row states and the request schemas shared by the API and the SPA.
 */
/** Limits of an upload (`.xlsx` or `.csv`). */
export const IMPORT_MAX_BYTES = 10 * 1024 * 1024;
export const IMPORT_MAX_ROWS = 20_000;
/** Up to this many rows the import runs in the request; above it as the job `call_list.import`. */
export const IMPORT_SYNC_ROWS = 2_000;
export const IMPORT_MAX_COLUMNS = 100;
export const IMPORT_SAMPLE_ROWS = 10;
export const importFields = [
    'first_name',
    'last_name',
    'full_name',
    'phone',
    'email',
    'street',
    'postal_code',
    'city',
    'note',
    'extra',
    'ignore',
];
/** Options of the „Spalten zuordnen" select. */
export const importFieldLabels = {
    first_name: 'Vorname',
    last_name: 'Nachname',
    full_name: 'Name (Vor- und Nachname)',
    phone: 'Telefon',
    email: 'E-Mail',
    street: 'Straße',
    postal_code: 'PLZ',
    city: 'Ort',
    note: 'Notiz',
    extra: 'Zusatzinfo',
    ignore: 'Ignorieren',
};
/** Fields a file may map at most once (Zusatzinfo and Ignorieren any number of times). */
export const singleImportFields = [
    'first_name',
    'last_name',
    'full_name',
    'phone',
    'email',
    'street',
    'postal_code',
    'city',
    'note',
];
const headerAliases = {
    first_name: ['vorname', 'firstname', 'givenname', 'rufname'],
    last_name: ['nachname', 'lastname', 'surname', 'familienname', 'zuname'],
    full_name: ['name', 'fullname', 'vollername', 'vollstaendigername', 'kontakt', 'kunde', 'ansprechpartner'],
    phone: [
        'telefon',
        'telefonnummer',
        'tel',
        'phone',
        'phonenumber',
        'mobil',
        'mobilnummer',
        'mobile',
        'handy',
        'handynummer',
        'rufnummer',
        'festnetz',
    ],
    email: ['email', 'mail', 'emailadresse', 'mailadresse'],
    street: ['strasse', 'street', 'adresse', 'anschrift', 'strasseundhausnummer', 'strassehausnummer'],
    postal_code: ['plz', 'postleitzahl', 'zip', 'zipcode', 'postalcode', 'postcode'],
    city: ['ort', 'stadt', 'city', 'wohnort'],
    note: ['notiz', 'notizen', 'bemerkung', 'bemerkungen', 'kommentar', 'anmerkung', 'note', 'notes'],
};
/** „E-Mail Adresse" → „emailadresse", „Straße" → „strasse". */
export function normalizeHeader(header) {
    return header
        .toLowerCase()
        .replaceAll('ä', 'ae')
        .replaceAll('ö', 'oe')
        .replaceAll('ü', 'ue')
        .replaceAll('ß', 'ss')
        .replace(/[^a-z0-9]/g, '');
}
/**
 * Suggested field per column from the header names: exact alias matches first, then headers that start with an
 * alias („Telefon privat", „Handy-Nr."); every single field once (first column wins), the rest „Zusatzinfo", empty
 * headers „Ignorieren".
 */
export function suggestImportMapping(headers) {
    const keys = headers.map(normalizeHeader);
    const result = keys.map((key) => (key === '' ? 'ignore' : 'extra'));
    const taken = new Set();
    const fields = Object.keys(headerAliases);
    const assign = (matches) => {
        for (const field of fields) {
            if (taken.has(field))
                continue;
            const index = keys.findIndex((key, column) => result[column] === 'extra' &&
                key !== '' &&
                headerAliases[field].some((alias) => matches(key, alias)));
            if (index >= 0) {
                result[index] = field;
                taken.add(field);
            }
        }
    };
    assign((key, alias) => key === alias);
    assign((key, alias) => alias.length >= 3 && key.startsWith(alias));
    // A full name next to first and last name is a duplicate of both: keep the split columns.
    if (taken.has('full_name') && (taken.has('first_name') || taken.has('last_name'))) {
        const index = result.indexOf('full_name');
        if (index >= 0)
            result[index] = 'extra';
    }
    return result;
}
/** The German error of a mapping, or null when it can be imported. */
export function importMappingError(mapping) {
    if (!mapping.includes('phone'))
        return 'Bitte ordnen Sie eine Spalte „Telefon“ zu.';
    for (const field of singleImportFields) {
        if (mapping.filter((value) => value === field).length > 1) {
            return `„${importFieldLabels[field]}“ ist mehreren Spalten zugeordnet.`;
        }
    }
    return null;
}
export const importMappingSchema = z.array(z.enum(importFields)).min(1).max(IMPORT_MAX_COLUMNS);
/** List fields of `POST /dialer/lists/import` (the multipart field `list`, JSON). */
export const importListFieldsSchema = z.object({
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120),
    eventTypeId: z.string().uuid('Bitte wählen Sie einen Ereignistyp.'),
    setterIds: z.array(z.string().uuid()).max(50).optional(),
    campaignId: z.string().uuid().nullable().optional(),
    sortRule: z.enum(callListSortRules).optional(),
    followUpsFirst: z.boolean().optional(),
    timeWindowStart: timeOfDaySchema.optional(),
    timeWindowEnd: timeOfDaySchema.optional(),
});
/** How a file row is treated (preview and import). */
export const importRowOutcomes = [
    'new',
    'existing',
    'blocked',
    'invalid_phone',
    'duplicate_in_file',
];
export const importRowOutcomeLabels = {
    new: 'Neu',
    existing: 'Bereits Kontakt',
    blocked: 'Gesperrt',
    invalid_phone: 'Ungültige Telefonnummer',
    duplicate_in_file: 'Doppelt in der Datei',
};
/** Status of a row in the import list (Liste › Kontakte). */
export const importRowStatuses = ['open', 'attempted', 'promoted', 'wrong_number', 'blocked'];
/** Filter chips of the import list table; „Versuche n" is shown per row („Versuche 3"). */
export const importRowStatusLabels = {
    open: 'Offen',
    attempted: 'Versuche',
    promoted: 'Übernommen',
    wrong_number: 'Falsche Nummer',
    blocked: 'Gesperrt',
};
export const importRowFilters = ['all', ...importRowStatuses];
/** Dispositions that promote a staged contact (the person was reached); `block` promotes and sets DNC. */
export const promotingDispositions = [
    'appointment_set',
    'follow_up',
    'callback_requested',
    'documents_promised',
    'no_interest',
    'block',
];
//# sourceMappingURL=dialer-import.js.map